// src/pages/screens/SummaryScreen.js
import { useEffect, useState } from "react";
import { useDatabase } from "../databaseContext";
import { Row, Col, Spin } from "antd";
import { Database, Network, Server, Table } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * SummaryScreen
 */
const SummaryScreen = ({ showNotification, onTabChange }) => {
  const {
    dbList,
    setDbList,
    connections,
    dbEndpoints,
    tablesMap,
    fetchDbList, // (filters = [], limit, pageId) => { data, totalCount, totalPages }
    fetchConnections,
    fetchDbEndpoints,
    fetchDbNames,
    fetchTablesForDb,
  } = useDatabase();

  const [loadingSummary, setLoadingSummary] = useState(true);

  /**
   * 1) On first mount, fetch ALL db pages,
   * 2) Then fetch tables for each DB,
   * 3) Then fetch endpoints, connections, etc.
   */
  useEffect(() => {
    (async () => {
      try {
        // 1) fetch all DB pages so we have the complete dbList
        await fetchAllDatabases();

        // 2) now fetch all tables for each DB in parallel
        await fetchAllTablesForAllDbs();

        // 3) also fetch other data
        await fetchTablesForDb();
        await fetchConnections();
        await fetchDbEndpoints();
        await fetchDbNames();
      } catch (err) {
        console.error("Error fetching summary data:", err?.response?.status, err?.message);
      } finally {
        setLoadingSummary(false); // done loading
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Helper: fetch all DBs across all pages, storing them in context
   */
  const fetchAllDatabases = async () => {
    let page = 1;
    let combined = [];
    while (true) {
      const { data, totalPages } = await fetchDbList([], 10, page);
      combined.push(...(data || []));
      if (page >= totalPages) break;
      page++;
    }
    setDbList(combined);
  };

  /**
   * Helper: fetch tables for each DB in parallel
   */
    const fetchAllTablesForAllDbs = async () => {
      if (!dbList || dbList.length === 0) return;
      const promises =  fetchTablesForDb();
        await Promise.all(promises);
    };

  /**
   * Re-fetch all DB data again (if user clicks the "Total Databases" card)
   */
  const fetchDatabaseData = async () => {
    setLoadingSummary(true);
    try {
      await fetchAllDatabases();
    } catch (err) {
      console.error("Error re-fetching all databases:", err?.response?.status, err?.message);
    } finally {
      setLoadingSummary(false);
    }
  };

  /**
   * Re-fetch all VMs
   */
  const loadVMData = async () => {
    setLoadingSummary(true);
    try {
      await fetchDbEndpoints();
    } catch (err) {
      console.error("Error fetching endpoints:", err?.response?.status, err?.message);
    } finally {
      setLoadingSummary(false);
    }
  };

  /**
   * Re-fetch all connections
   */
  const fetchConnectionData = async () => {
    setLoadingSummary(true);
    try {
      await fetchConnections();
    } catch (err) {
      console.error("Error fetching connections:", err?.response?.status, err?.message);
    } finally {
      setLoadingSummary(false);
    }
  };

  /**
   * Re-fetch all tables again for all DBs
   */
  const loadTableData = async () => {
    setLoadingSummary(true);
    try {
      await fetchAllTablesForAllDbs();
    } catch (err) {
      console.error("Error fetching all table data:", err?.response?.status, err?.message);
    } finally {
      setLoadingSummary(false);
    }
  };

  // ---------------------------------------------------------------------------------------
  // If still loading, show a spinner (or skeleton), so we never show zero prematurely.
  // ---------------------------------------------------------------------------------------
  if (loadingSummary) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 300 }}>
        <Spin size="large" tip="Loading your summary..." />
      </div>
    );
  }

  // ---------------------------------------------------------------------------------------
  // Once loaded, we have correct DB list, tables, endpoints, connections in context
  // ---------------------------------------------------------------------------------------
  const totalDbs = dbList.length;
  const activeDbsCount = dbList.filter((db) => db.status === "ACTIVE").length;
  const inactiveDbsCount = dbList.filter((db) => db.status !== "ACTIVE").length;

  // sum total tables
  const totalTables = Object.values(tablesMap).reduce((sum, arr) => sum + arr.length, 0);

  // metrics array
  const metrics = [
    {
      title: "Total Databases",
      value: totalDbs,
      icon: <Database className="h-6 w-6 text-blue-500" />,
      bgColor: "bg-blue-50",
    },
    {
      title: "Active Connections",
      value: connections.length,
      icon: <Network className="h-6 w-6 text-emerald-500" />,
      bgColor: "bg-emerald-50",
    },
    {
      title: "Virtual Machines",
      value: dbEndpoints.length,
      icon: <Server className="h-6 w-6 text-purple-500" />,
      bgColor: "bg-purple-50",
    },
    {
      title: "Total Tables",
      value: totalTables,
      icon: <Table className="h-6 w-6 text-amber-500" />,
      bgColor: "bg-amber-50",
    },
  ];

  return (
    <div className="p-6">
      {/* Top row of summary cards */}
      <Row gutter={[16, 16]}>
        {metrics.map((metric, index) => (
          <Col xs={12} md={6} key={index}>
            <div
              className={`bg-white rounded-lg shadow p-4 border-l-4 ${
                metric.title === "Total Databases"
                  ? "border-blue-500"
                  : metric.title === "Active Connections"
                  ? "border-emerald-500"
                  : metric.title === "Virtual Machines"
                  ? "border-purple-500"
                  : "border-amber-500"
              } cursor-pointer hover:shadow-md transition-shadow`}
              onClick={() => {
                onTabChange(
                  metric.title === "Total Databases"
                    ? "databases"
                    : metric.title === "Active Connections"
                    ? "connections"
                    : metric.title === "Virtual Machines"
                    ? "vms"
                    : "tables"
                );
                // trigger relevant data load
                if (metric.title === "Total Databases") fetchDatabaseData();
                if (metric.title === "Active Connections") fetchConnectionData();
                if (metric.title === "Virtual Machines") loadVMData();
                if (metric.title === "Total Tables") loadTableData();
              }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{metric.title}</p>
                  <div className="flex items-baseline mt-1">
                    <p className="text-2xl font-semibold text-gray-900 m-0">{metric.value}</p>
                  </div>
                </div>
                <div className={`p-3 rounded-full ${metric.bgColor}`}>{metric.icon}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Additional Overview */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Database Environment Overview</h2>
        <p className="text-gray-600 mb-6">
          Welcome to your database dashboard. Click on any card above or tab to view detailed
          information.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Database Status */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center">
              <Database className="h-5 w-5 mr-2 text-blue-500" />
              Database Status
            </h3>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Active Databases:</span>
              <span className="font-medium">{activeDbsCount}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Inactive Databases:</span>
              <span className="font-medium">{inactiveDbsCount}</span>
            </div>
            <div className="mt-4">
              <button
                onClick={() => {
                  onTabChange("databases");
                  fetchDatabaseData();
                }}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
              >
                View all databases
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* VM Agents */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center">
              <Server className="h-5 w-5 mr-2 text-purple-500" />
              VM Agents
            </h3>
            <p className="text-gray-600 mb-3">
              You have {dbEndpoints.length} VM agents installed across your environment.
            </p>
            <div className="mt-4">
              <button
                onClick={() => {
                  onTabChange("vms");
                  loadVMData();
                }}
                className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center"
              >
                View VM agents
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Connections */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center">
              <Network className="h-5 w-5 mr-2 text-yellow-500" />
              Connections
            </h3>
            <p className="text-gray-600 mb-3">
              You have {connections.length} active connections to your databases.
            </p>
            <div className="mt-4">
              <button
                onClick={() => {
                  onTabChange("connections");
                  fetchConnectionData();
                }}
                className="text-yellow-600 hover:text-yellow-800 text-sm font-medium flex items-center"
              >
                View connections
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Tables */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center">
              <Table className="h-5 w-5 mr-2 text-indigo-500" />
              Tables
            </h3>
            <p className="text-gray-600 mb-3">
              You have {totalTables} tables across all your databases.
            </p>
            <div className="mt-4">
              <button
                onClick={() => {
                  onTabChange("tables");
                  loadTableData();
                }}
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
              >
                View tables
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 border-t pt-6">
          <h3 className="font-medium mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/pam/databases/add/linux"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Install Agent
            </Link>

            <Link
              to="/pam/policy/v2/createPolicy"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              Create Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryScreen;
